import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Users, ChartBar, CreditCard, Airplane, CheckCircle, Clock, MapPin, BellRinging } from '@phosphor-icons/react';

const SCREENS = [
  { id: 'dashboard', label: 'Dashboard', url: 'trackmypackage.app/dashboard' },
  { id: 'packages', label: 'Packages', url: 'trackmypackage.app/packages' },
  { id: 'portal', label: 'Client Portal', url: 'portal.trackmypackage.app' },
  { id: 'invoices', label: 'Invoices', url: 'trackmypackage.app/invoices' },
];

const DashboardScreen = () => (
  <div className="flex h-full">
    <div className="w-44 bg-white border-r border-gray-200 p-3 hidden sm:block">
      <div className="flex items-center gap-2 mb-5">
        <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-7 w-7 rounded-lg object-contain" />
        <span className="text-[11px] font-bold text-gray-800">Track My Package</span>
      </div>
      {['Dashboard', 'Packages', 'Customers', 'Invoices', 'Settings'].map((item, i) => (
        <div key={item} className={`px-2.5 py-1.5 rounded-lg text-[11px] mb-0.5 ${i === 0 ? 'bg-[#003166] text-white font-semibold' : 'text-gray-400'}`}>{item}</div>
      ))}
    </div>
    <div className="flex-1 p-4">
      <p className="text-sm font-bold text-gray-800 mb-3" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>DASHBOARD</p>
      <div className="grid grid-cols-4 gap-2.5 mb-4">
        {[
          { label: 'Total Packages', val: '247', color: '#003166', icon: Package },
          { label: 'Active Clients', val: '86', color: '#0080FF', icon: Users },
          { label: 'Revenue', val: '$4,820', color: '#FF9900', icon: ChartBar },
          { label: 'Pending', val: '12', color: '#10B981', icon: Clock },
        ].map((kpi, i) => (
          <motion.div key={i} className="bg-white rounded-lg p-2.5 border border-gray-100 shadow-sm"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
            <div className="flex items-center gap-1.5">
              <kpi.icon size={12} style={{ color: kpi.color }} weight="fill" />
              <p className="text-[9px] text-gray-400 uppercase font-semibold">{kpi.label}</p>
            </div>
            <p className="text-lg font-bold mt-0.5" style={{ color: kpi.color, fontFamily: 'Bebas Neue' }}>{kpi.val}</p>
          </motion.div>
        ))}
      </div>
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-[10px] font-bold text-gray-700 uppercase">Recent Packages</p>
        </div>
        <table className="w-full text-[11px]">
          <thead className="bg-gray-50"><tr>{['Tracking', 'Client', 'Weight', 'Status', 'Cost'].map(h => <th key={h} className="px-2.5 py-1.5 text-left text-[9px] text-gray-400 uppercase">{h}</th>)}</tr></thead>
          <tbody>
            {[
              { tn: '1Z999AA1012', name: 'Maria Lopez', w: '5.2 lbs', status: 'In Transit', sc: '$23.40', sColor: '#FF9900' },
              { tn: '9400111899', name: 'Carlos Rivera', w: '8.0 lbs', status: 'Delivered', sc: '$36.00', sColor: '#10B981' },
              { tn: 'FX74893015', name: 'Ana Martinez', w: '3.5 lbs', status: 'At Warehouse', sc: '$15.75', sColor: '#0080FF' },
            ].map((row, i) => (
              <motion.tr key={i} className="border-t border-gray-50" initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.08 }}>
                <td className="px-2.5 py-1.5 font-mono text-gray-700">{row.tn}</td>
                <td className="px-2.5 py-1.5 text-gray-600">{row.name}</td>
                <td className="px-2.5 py-1.5 text-gray-500">{row.w}</td>
                <td className="px-2.5 py-1.5"><span className="px-1.5 py-0.5 rounded-full text-[8px] font-semibold text-white" style={{ background: row.sColor }}>{row.status}</span></td>
                <td className="px-2.5 py-1.5 font-semibold text-gray-700">{row.sc}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const PackagesScreen = () => (
  <div className="flex h-full">
    <div className="w-44 bg-white border-r border-gray-200 p-3 hidden sm:block">
      <div className="flex items-center gap-2 mb-5">
        <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-7 w-7 rounded-lg object-contain" />
        <span className="text-[11px] font-bold text-gray-800">Track My Package</span>
      </div>
      {['Dashboard', 'Packages', 'Customers', 'Invoices', 'Settings'].map((item, i) => (
        <div key={item} className={`px-2.5 py-1.5 rounded-lg text-[11px] mb-0.5 ${i === 1 ? 'bg-[#003166] text-white font-semibold' : 'text-gray-400'}`}>{item}</div>
      ))}
    </div>
    <div className="flex-1 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-gray-800" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>PACKAGES</p>
        <div className="px-3 py-1.5 bg-[#003166] text-white text-[10px] font-semibold rounded-lg flex items-center gap-1">
          <Package size={12} /> New Package
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        {[{ label: 'All', count: 247, active: true }, { label: 'In Transit', count: 34 }, { label: 'Delivered', count: 189 }, { label: 'Ready', count: 12 }].map((t, i) => (
          <motion.div key={i} className={`px-2.5 py-1 rounded-lg text-[10px] font-medium ${t.active ? 'bg-[#003166] text-white' : 'bg-gray-100 text-gray-500'}`}
            initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            {t.label} ({t.count})
          </motion.div>
        ))}
      </div>
      {[
        { tn: '1Z999AA1012', name: 'Maria Lopez', status: 'In Transit', steps: 3, current: 2, color: '#FF9900' },
        { tn: '9400111899223', name: 'Carlos Rivera', status: 'Delivered', steps: 5, current: 5, color: '#10B981' },
        { tn: 'FX748930158', name: 'Ana Martinez', status: 'At Warehouse', steps: 5, current: 1, color: '#0080FF' },
        { tn: 'TBA612847293', name: 'David Chen', status: 'Ready for Pickup', steps: 4, current: 4, color: '#8B5CF6' },
      ].map((pkg, i) => (
        <motion.div key={i} className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm mb-2"
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-mono font-bold text-gray-700">{pkg.tn}</p>
              <p className="text-[10px] text-gray-400">{pkg.name}</p>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[8px] font-semibold text-white" style={{ background: pkg.color }}>{pkg.status}</span>
          </div>
          <div className="flex gap-0.5 mt-2">
            {Array.from({ length: pkg.steps }).map((_, s) => (
              <motion.div key={s} className="h-1 flex-1 rounded-full"
                style={{ background: s < pkg.current ? pkg.color : '#e5e7eb' }}
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.5 + i * 0.1 + s * 0.05 }} />
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const PortalScreen = () => (
  <div className="bg-gradient-to-b from-[#003166] to-[#001a3a] h-full p-5">
    <div className="max-w-sm mx-auto">
      <motion.div className="text-center mb-4" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="w-10 h-10 rounded-xl mx-auto mb-2 object-contain" />
        <p className="text-white font-bold text-sm">Track My Package</p>
        <p className="text-white/50 text-[10px]">Hi Maria! Track your packages below</p>
      </motion.div>
      <motion.div className="bg-white/10 backdrop-blur rounded-xl p-3 mb-3 border border-white/10"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <p className="text-[10px] text-white/60 mb-1.5 font-medium">Add Tracking Number</p>
        <div className="flex gap-2">
          <div className="flex-1 bg-white/10 rounded-lg px-2.5 py-1.5 text-[11px] text-white/40">Enter tracking number...</div>
          <div className="px-3 py-1.5 bg-[#FF9900] rounded-lg text-[10px] text-white font-semibold">Submit</div>
        </div>
      </motion.div>
      <motion.div className="flex items-center gap-2 bg-rose-500/15 border border-rose-500/20 rounded-xl p-2.5 mb-3"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>
        <BellRinging size={14} className="text-rose-400" weight="fill" />
        <p className="text-[9px] text-white/70">Push notifications enabled</p>
        <CheckCircle size={12} className="text-emerald-400 ml-auto" weight="fill" />
      </motion.div>
      {[
        { tn: '1Z999AA1012', status: 'In Transit', color: '#FF9900', icon: Airplane },
        { tn: '9400111899', status: 'Delivered', color: '#10B981', icon: CheckCircle },
        { tn: 'FX74893015', status: 'Ready for Pickup', color: '#8B5CF6', icon: MapPin },
      ].map((p, i) => (
        <motion.div key={i} className="bg-white/5 border border-white/10 rounded-xl p-2.5 mb-2 flex items-center gap-2.5"
          initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${p.color}20` }}>
            <p.icon size={14} style={{ color: p.color }} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-mono text-white/80">{p.tn}</p>
            <p className="text-[9px] text-white/40">{p.status}</p>
          </div>
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
        </motion.div>
      ))}
    </div>
  </div>
);

const InvoicesScreen = () => (
  <div className="flex h-full">
    <div className="w-44 bg-white border-r border-gray-200 p-3 hidden sm:block">
      <div className="flex items-center gap-2 mb-5">
        <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-7 w-7 rounded-lg object-contain" />
        <span className="text-[11px] font-bold text-gray-800">Track My Package</span>
      </div>
      {['Dashboard', 'Packages', 'Customers', 'Invoices', 'Settings'].map((item, i) => (
        <div key={item} className={`px-2.5 py-1.5 rounded-lg text-[11px] mb-0.5 ${i === 3 ? 'bg-[#003166] text-white font-semibold' : 'text-gray-400'}`}>{item}</div>
      ))}
    </div>
    <div className="flex-1 p-4">
      <p className="text-sm font-bold text-gray-800 mb-3" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.05em' }}>INVOICES</p>
      <div className="grid grid-cols-3 gap-2.5 mb-4">
        {[
          { label: 'Total Invoiced', val: '$12,450', color: '#003166' },
          { label: 'Paid', val: '$10,820', color: '#10B981' },
          { label: 'Pending', val: '$1,630', color: '#FF9900' },
        ].map((kpi, i) => (
          <motion.div key={i} className="bg-white rounded-lg p-2.5 border border-gray-100 shadow-sm"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <p className="text-[9px] text-gray-400 uppercase font-semibold">{kpi.label}</p>
            <p className="text-lg font-bold" style={{ color: kpi.color, fontFamily: 'Bebas Neue' }}>{kpi.val}</p>
          </motion.div>
        ))}
      </div>
      {[
        { inv: 'INV-2024001', name: 'Maria Lopez', amount: '$85.50', status: 'Paid', color: '#10B981', method: 'Stripe' },
        { inv: 'INV-2024002', name: 'Carlos Rivera', amount: '$42.00', status: 'Pending', color: '#FF9900', method: '' },
        { inv: 'INV-2024003', name: 'Ana Martinez', amount: '$156.75', status: 'Paid', color: '#10B981', method: 'PayPal' },
      ].map((row, i) => (
        <motion.div key={i} className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm mb-2 flex items-center gap-3"
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.1 }}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-bold text-gray-700">{row.inv}</p>
              <span className="px-1.5 py-0.5 rounded-full text-[8px] font-semibold text-white" style={{ background: row.color }}>{row.status}</span>
            </div>
            <p className="text-[10px] text-gray-400">{row.name}</p>
          </div>
          <p className="text-sm font-bold text-gray-800">{row.amount}</p>
          {row.status === 'Pending' ? (
            <div className="flex gap-1">
              <div className="px-2 py-1 bg-[#635BFF] rounded text-[8px] text-white font-semibold">Stripe</div>
              <div className="px-2 py-1 bg-[#0070BA] rounded text-[8px] text-white font-semibold">PayPal</div>
            </div>
          ) : (
            <div className="px-2 py-1 bg-gray-100 rounded text-[8px] text-gray-500 font-medium">{row.method}</div>
          )}
        </motion.div>
      ))}
      <motion.div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mt-3"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
        <p className="text-[9px] text-blue-600 font-medium">Auto-generated invoices appear here when packages reach "Ready for Pickup"</p>
      </motion.div>
    </div>
  </div>
);

const SCREEN_COMPONENTS = { dashboard: DashboardScreen, packages: PackagesScreen, portal: PortalScreen, invoices: InvoicesScreen };

export default function AnimatedDemo() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => setActiveIdx(prev => (prev + 1) % SCREENS.length), 4500);
    return () => clearInterval(timer);
  }, [isPaused]);

  const ActiveScreen = SCREEN_COMPONENTS[SCREENS[activeIdx].id];

  return (
    <div className="relative max-w-4xl mx-auto" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      {/* Screen tabs */}
      <div className="flex gap-1 mb-3 justify-center">
        {SCREENS.map((s, i) => (
          <button key={s.id} onClick={() => setActiveIdx(i)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 ${i === activeIdx ? 'bg-[#FF9900] text-white shadow-lg shadow-orange-500/20' : 'bg-white/5 text-gray-500 hover:text-gray-300'}`}
            data-testid={`demo-tab-${s.id}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Progress bar */}
      <div className="flex gap-1 mb-4 max-w-xs mx-auto">
        {SCREENS.map((_, i) => (
          <div key={i} className="h-0.5 flex-1 rounded-full bg-white/10 overflow-hidden">
            <motion.div className="h-full rounded-full" style={{ background: i === activeIdx ? '#FF9900' : i < activeIdx ? '#FF9900' : 'transparent' }}
              initial={{ width: '0%' }} animate={{ width: i === activeIdx ? '100%' : i < activeIdx ? '100%' : '0%' }}
              transition={{ duration: i === activeIdx ? 4.5 : 0, ease: 'linear' }} key={`${activeIdx}-${i}`} />
          </div>
        ))}
      </div>

      {/* Browser chrome */}
      <div className="bg-[#1a1f2e] rounded-t-xl border border-white/10 border-b-0 px-4 py-2 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        </div>
        <div className="flex-1 ml-3 bg-white/5 rounded-lg px-3 py-1 text-[11px] text-gray-500 font-mono">
          {SCREENS[activeIdx].url}
        </div>
      </div>

      {/* Screen content */}
      <div className="bg-[#f8f9fc] border border-white/10 rounded-b-xl overflow-hidden shadow-2xl shadow-black/40" style={{ minHeight: '380px' }}>
        <AnimatePresence mode="wait">
          <motion.div key={SCREENS[activeIdx].id}
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }} style={{ minHeight: '380px' }}>
            <ActiveScreen />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Glow */}
      <div className="absolute -inset-4 bg-gradient-to-r from-[#0080FF]/10 via-[#FF9900]/5 to-[#0080FF]/10 rounded-2xl blur-xl -z-10 pointer-events-none" />
    </div>
  );
}
